import {useMemo, useCallback} from 'react';
import {IProductOption} from '../../../../../types/galleryTypes';
import {IProductSelectionAvailabilityMap} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import {useGlobals} from '../../../../../globalPropsContext';
import {Experiments} from '../../../../../constants';

interface UseOptionSelectionLogicParams {
  option: IProductOption;
  selectionIds: number[];
  selectionsAvailability: IProductSelectionAvailabilityMap[string];
}

export const useOptionSelectionLogic = ({
  option,
  selectionIds,
  selectionsAvailability,
}: UseOptionSelectionLogicParams) => {
  const {useExperiments, textsMap} = useGlobals();
  const showHiddenVariants = useExperiments.enabled(Experiments.ShowHiddenVariants);

  const visibleSelections = useMemo(
    () =>
      option?.selections?.filter(
        (selection) => showHiddenVariants || selectionsAvailability?.[selection.id]?.isVisible
      ) || [],
    [option.selections, selectionsAvailability, showHiddenVariants]
  );

  const selectedSelectionId = useMemo(
    () => option?.selections?.find((selection) => selectionIds?.includes(selection.id))?.id.toString(),
    [option.selections, selectionIds]
  );

  const isSelectionDisabled = useCallback(
    (selectionId: number) => !showHiddenVariants && !selectionsAvailability?.[selectionId]?.isSelectable,
    [selectionsAvailability, showHiddenVariants]
  );

  const isSelectionUnreachable = useCallback(
    (selectionId: number) =>
      !selectionsAvailability?.[selectionId]?.isSelectable ||
      (showHiddenVariants && selectionsAvailability?.[selectionId]?.isVisible === false),
    [selectionsAvailability, showHiddenVariants]
  );

  const isSelectionMarkedUnavailable = useCallback(
    (selectionId: number) => showHiddenVariants && isSelectionUnreachable(selectionId),
    [showHiddenVariants, isSelectionUnreachable]
  );

  const getSelectionAriaLabel = useCallback(
    (selectionId: number, description: string) =>
      isSelectionMarkedUnavailable(selectionId) ? `${description}, ${textsMap.productUnavailableText}` : description,
    [isSelectionMarkedUnavailable, textsMap.productUnavailableText]
  );

  const isSelectionChecked = useCallback((selectionId: number) => selectionIds?.includes(selectionId), [selectionIds]);

  const calculateNextSelectionIds = useCallback(
    (pickedSelectionId: number) => {
      const currentSelectionIds = selectionIds ?? /* istanbul ignore next */ [];
      const unchangedSelections = currentSelectionIds.filter(
        (selectionId) => !visibleSelections?.find(({id}) => id === selectionId)
      );
      return [...unchangedSelections, pickedSelectionId];
    },
    [selectionIds, visibleSelections]
  );

  return {
    visibleSelections,
    selectedSelectionId,
    isSelectionDisabled,
    isSelectionUnreachable,
    isSelectionMarkedUnavailable,
    getSelectionAriaLabel,
    isSelectionChecked,
    calculateNextSelectionIds,
  };
};
